// Booking-specific React Hooks
// Provides easy-to-use hooks for booking-related API calls
import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi, useApiState, usePagination, useApiMutation, useInfiniteScroll } from './useApi.js';
import { bookingService } from '../services';

// Booking Management Hooks
export const useCreateBooking = () => {
  return useApiMutation();
};

export const useBookings = (params = {}) => {
  return usePagination(bookingService.getAllBookings.bind(bookingService), params);
};

export const useBookingById = (bookingId) => {
  return useApi(
    bookingService.getBookingById.bind(bookingService, bookingId),
    [bookingId]
  );
};

export const useUpdateBookingStatus = () => {
  return useApiMutation();
};

export const useCancelBooking = () => {
  return useApiMutation();
};

export const useBookingHistory = (params = {}) => {
  return usePagination(bookingService.getBookingHistory.bind(bookingService), params);
};

// Booking Analytics Hooks
export const useBookingAnalytics = (params = {}) => {
  return useApi(
    bookingService.getAnalytics.bind(bookingService, params),
    [params]
  );
};

export const useBookingTrends = (params = {}) => {
  return useApi(
    bookingService.getBookingTrends.bind(bookingService, params),
    [params]
  );
};

export const useBookingRevenueAnalytics = (params = {}) => {
  return useApi(
    bookingService.getRevenueAnalytics.bind(bookingService, params),
    [params]
  );
};

export const useBookingPerformanceMetrics = (params = {}) => {
  return useApi(
    bookingService.getPerformanceMetrics.bind(bookingService, params),
    [params]
  );
};

// Booking Types & Categories Hooks
export const useBookingTypes = () => {
  return useApi(bookingService.getBookingTypes.bind(bookingService));
};

export const useBookingCategories = () => {
  return useApi(bookingService.getBookingCategories.bind(bookingService));
};

export const useEmergencyTypes = () => {
  return useApi(bookingService.getEmergencyTypes.bind(bookingService));
};

// Booking Status Management Hooks
export const useBookingStatuses = () => {
  return useApi(bookingService.getBookingStatuses.bind(bookingService));
};

export const useUpdateBookingProgress = () => {
  return useApiMutation();
};

export const useCompleteBooking = () => {
  return useApiMutation();
};

// Booking Assignments Hooks
export const useAssignDriver = () => {
  return useApiMutation();
};

export const useAssignAmbulance = () => {
  return useApiMutation();
};

export const useReassignBooking = () => {
  return useApiMutation();
};

export const useAvailableDrivers = (bookingId, params = {}) => {
  return useApi(
    bookingService.getAvailableDrivers.bind(bookingService, bookingId, params),
    [bookingId, params]
  );
};

export const useAvailableAmbulances = (bookingId, params = {}) => {
  return useApi(
    bookingService.getAvailableAmbulances.bind(bookingService, bookingId, params),
    [bookingId, params]
  );
};

// Booking Location & Routing Hooks
export const useUpdateBookingLocation = () => {
  return useApiMutation();
};

export const useBookingRoute = (bookingId, autoExecute = false) => {
  // Fetch historical route data for route playback
  // Returns: { data: [{ latitude, longitude, timestamp, speed, heading }, ...], loading, error, execute }
  return useApi(
    bookingService.getBookingRoute.bind(bookingService, bookingId),
    [bookingId],
    autoExecute
  );
};

export const useUpdateBookingRoute = () => {
  return useApiMutation();
};

export const useEstimatedArrival = (bookingId) => {
  return useApi(
    bookingService.getEstimatedArrival.bind(bookingService, bookingId),
    [bookingId]
  );
};

// Booking Payments Hooks
export const useProcessPayment = () => {
  return useApiMutation();
};

export const usePaymentStatus = (bookingId) => {
  return useApi(
    bookingService.getPaymentStatus.bind(bookingService, bookingId),
    [bookingId]
  );
};

export const useRefundBooking = () => {
  return useApiMutation();
};

export const usePaymentHistory = (bookingId) => {
  return useApi(
    bookingService.getPaymentHistory.bind(bookingService, bookingId),
    [bookingId]
  );
};

// Booking Communication Hooks
export const useSendBookingUpdate = () => {
  return useApiMutation();
};

export const useBookingMessages = (bookingId) => {
  return useApi(
    bookingService.getBookingMessages.bind(bookingService, bookingId),
    [bookingId]
  );
};

export const useSendMessage = () => {
  return useApiMutation();
};

// Booking Ratings & Feedback Hooks
export const useSubmitRating = () => {
  return useApiMutation();
};

export const useBookingRating = (bookingId) => {
  return useApi(
    bookingService.getBookingRating.bind(bookingService, bookingId),
    [bookingId]
  );
};

export const useSubmitFeedback = () => {
  return useApiMutation();
};

export const useBookingFeedback = (bookingId) => {
  return useApi(
    bookingService.getBookingFeedback.bind(bookingService, bookingId),
    [bookingId]
  );
};

// Booking Reports Hooks
export const useGenerateBookingReport = () => {
  return useApiMutation();
};

export const useBookingReportHistory = (params = {}) => {
  return usePagination(bookingService.getBookingReportHistory.bind(bookingService), params);
};

export const useDownloadBookingReport = () => {
  return useApiMutation();
};

// Booking Search & Filters Hooks
export const useSearchBookings = () => {
  return useApiMutation();
};

export const useBookingFilters = () => {
  return useApi(bookingService.getBookingFilters.bind(bookingService));
};

export const useBookingStats = (params = {}) => {
  return useApi(
    bookingService.getBookingStats.bind(bookingService, params),
    [params]
  );
};

// Emergency Bookings Hooks
export const useCreateEmergencyBooking = () => {
  return useApiMutation();
};

export const useEmergencyBookings = (params = {}) => {
  return usePagination(bookingService.getEmergencyBookings.bind(bookingService), params);
};

export const usePrioritizeEmergencyBooking = () => {
  return useApiMutation();
};

// Booking Scheduling Hooks
export const useScheduleBooking = () => {
  return useApiMutation();
};

export const useScheduledBookings = (params = {}) => {
  return usePagination(bookingService.getScheduledBookings.bind(bookingService), params);
};

export const useRescheduleBooking = () => {
  return useApiMutation();
};

export const useCancelScheduledBooking = () => {
  return useApiMutation();
};

// Real-time Booking Updates Hook
export const useBookingUpdates = (bookingId) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookingId) return;

    const fetchUpdates = async () => {
      setLoading(true);
      try {
        const result = await bookingService.getBookingMessages(bookingId);
        setUpdates(result.data || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();

    // Set up polling for real-time updates
    const interval = setInterval(fetchUpdates, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [bookingId]);

  return { updates, loading, error };
};

// Combined Booking State Hook
export const useBookingState = () => {
  const bookings = useBookings();
  const analytics = useBookingAnalytics();
  const types = useBookingTypes();
  const categories = useBookingCategories();
  const statuses = useBookingStatuses();
  const filters = useBookingFilters();

  return {
    bookings,
    analytics,
    types,
    categories,
    statuses,
    filters
  };
};

export const useLiveTrackingData = (bookingId, pollingInterval = 3000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Fetch tracking data
  const fetchTrackingData = useCallback(async () => {
    if (!bookingId || !isMountedRef.current) return;
    
    console.log('📞📞📞 [useLiveTrackingData] Fetching tracking data for bookingId:', bookingId);
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await bookingService.getLiveTrackingData(bookingId);
      console.log('✅✅✅ [useLiveTrackingData] Received result:', {
        hasResult: !!result,
        resultKeys: result ? Object.keys(result) : [],
        hasData: !!result?.data,
        dataKeys: result?.data ? Object.keys(result.data) : [],
        hasTracking: !!result?.data?.tracking,
        trackingKeys: result?.data?.tracking ? Object.keys(result.data.tracking) : [],
        hasDriverLocation: !!result?.data?.tracking?.driverLocation,
        hasAmbulanceLocation: !!result?.data?.tracking?.ambulanceLocation,
        fullResult: JSON.stringify(result),
      });
      
      if (isMountedRef.current) {
        setData(result);
      }
      return result;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
      }
      console.error('❌❌❌ [useLiveTrackingData] Error fetching tracking data:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [bookingId]);

  // Initial fetch and setup polling
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    if (bookingId) {
      fetchTrackingData();
    }

    // Set up polling interval (3-5 seconds, default 4 seconds)
    if (bookingId && pollingInterval > 0) {
      intervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          fetchTrackingData();
        }
      }, pollingInterval);
    }

    // Cleanup on unmount or when bookingId changes
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [bookingId, pollingInterval, fetchTrackingData]);

  // Manual refetch function
  const refetch = useCallback(() => {
    return fetchTrackingData();
  }, [fetchTrackingData]);

  return {
    data,
    loading,
    error,
    refetch,
    execute: fetchTrackingData
  };
};

export const useBookingStatusHistory = (bookingId) => {
  const result = useApi(
    bookingService.getBookingStatusHistory.bind(bookingService, bookingId),
    [bookingId],
    true // auto-execute when bookingId changes
  );
  
  // Also provide refetch for backward compatibility
  return {
    ...result,
    refetch: result.refetch || result.execute
  };
};
